import React, { useEffect, useRef, useMemo } from "react";

const SidebarItems = (props) => {
  const { data, selectedIndex, searchHits, getContextInfo, setSelectedIndex, searchTerm } =
    props;

  // Keep refs to all rendered items so we can scroll the selected one into view
  const itemRefs = useRef([]);

  // Determine if we have exactly one non-empty search token
  const singleSearchKey = useMemo(() => {
    if (searchTerm == null) return null;
    const tokens = String(searchTerm)
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (tokens.length === 1) {
      return tokens[0];
    }
    return null;
  }, [searchTerm]);

  const getSrcFromBody = (body) => {
    if (!body) return null;
    let obj = body;
    if (typeof obj === 'string') {
      try { obj = JSON.parse(obj); } catch (_) { obj = null; }
    }
    if (!obj || typeof obj !== 'object') return null;
    const src = obj.entireEvent && typeof obj.entireEvent === 'object' ? obj.entireEvent : obj;
    return src && typeof src === 'object' ? src : null;
  };

  const getMatchedValueForItem = (body, key) => {
    if (!key) return undefined;
    const src = getSrcFromBody(body);
    if (!src) return undefined;
    if (Object.prototype.hasOwnProperty.call(src, key)) {
      return src[key];
    }
    return undefined;
  };

  const formatMatchValue = (val) => {
    try {
      let s;
      if (val == null) {
        s = '';
      } else if (typeof val === 'object') {
        s = JSON.stringify(val);
      } else {
        s = String(val);
      }
      if (s.length > 80) s = s.slice(0, 77) + '...';
      return s;
    } catch (_) {
      return '';
    }
  };

  useEffect(() => {
    const el = itemRefs.current?.[selectedIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      try {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } catch (e) {
        // Fallback for older browsers/environments
        el.scrollIntoView();
      }
    }
  }, [selectedIndex]);

  const getColorIndex = (callId) => {
    if (!callId || !Number.isFinite(Number(callId))) {
      return 0
    }
    const n = Number(callId);
    // Map callId into 1..8 range for consistent palette
    return (Math.abs(n) % 8) + 1;
  };

  // Extract event time (ms) from body/entireEvent with fallbacks
  const getEventTimeMs = (body) => {
    const src = body && (body.entireEvent || body);
    if (!src || typeof src !== 'object') return null;

    // Prefer numeric timestamp if available
    let ts = src["Event-Date-Timestamp"] || src["timestamp"] || src["eventTimestamp"];
    if (ts != null) {
      const n = Number(ts);
      if (Number.isFinite(n)) {
        // Heuristic: values larger than year 3000 in ms mean it's probably in microseconds
        // Typical FreeSWITCH Event-Date-Timestamp is microseconds.
        if (n > 1e13) return Math.round(n / 1000); // from microseconds to ms
        // If it's plausible seconds (10 or 13 digits), normalize to ms
        if (n < 1e11) return Math.round(n * 1000); // seconds -> ms
        return Math.round(n); // already ms
      }
    }

    // Fallback: parse local date string
    const localStr = src["Event-Date-Local"] || src["date_local"] || src["Event-Date-GMT"];
    if (localStr && typeof localStr === 'string') {
      let d = new Date(localStr);
      if (isNaN(d.getTime())) {
        // Try replacing space with T
        d = new Date(localStr.replace(' ', 'T'));
      }
      const ms = d.getTime();
      if (Number.isFinite(ms)) return ms;
    }

    return null;
  };

  const formatAbsoluteTime = (ms) => {
    try {
      const d = new Date(ms);
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (_) {
      return '';
    }
  };

  // Precompute timestamps for all items to simplify delta rendering
  const itemTimes = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((it) => getEventTimeMs(it?.body));
  }, [data]);

  // Precompute contiguous call group boundaries to ensure oldest=1 and newest=N even if list is newest-first
  const groupInfo = Array.isArray(data)
    ? (() => {
        const info = new Array(data.length);
        let i = 0;
        while (i < data.length) {
          const start = i;
          const callId = data[i]?.callId;
          let len = 1;
          i++;
          while (i < data.length && data[i]?.callId === callId) {
            len++;
            i++;
          }
          for (let k = 0; k < len; k++) {
            info[start + k] = { start, length: len };
          }
        }
        return info;
      })()
    : [];
  return (
    <>
      {data
        ? data.map((item, index) => {
            const prevCallId = index > 0 ? data[index - 1]?.callId : null;
            const isGroupStart = index === 0 || item.callId !== prevCallId;
            const info = groupInfo[index] || { start: index, length: 1 };
            const positionFromStart = index - info.start;
            const displayNumber = info.length - positionFromStart; // 1 = oldest

            const colorIndex = getColorIndex(item.callId);
            // Determine if there is a time gap (>1000ms) to the next (older) item within the same call group.
            const curMsForGap = itemTimes[index];
            const nextIsSameGroup = index < data.length - 1 && data[index + 1]?.callId === item.callId;
            const nextMsForGap = nextIsSameGroup ? itemTimes[index + 1] : null;
            const hasTimeGapToNext =
              nextIsSameGroup &&
              curMsForGap != null &&
              nextMsForGap != null &&
              Number.isFinite(curMsForGap) &&
              Number.isFinite(nextMsForGap) &&
              (curMsForGap - nextMsForGap) > 1000; // newer minus older > 1000ms

            // Determine filter state from usedWebhookFilter.isFilteredOutByWebhookFilter (if present)
            let isFilteredOutByWebhookFilter = undefined;
            try {
              const rawBody = item?.body;
              const obj = typeof rawBody === 'string' ? JSON.parse(rawBody) : rawBody;
              const u = obj && typeof obj === 'object' ? obj.usedWebhookFilter : undefined;
              if (u && typeof u === 'object' && 'isFilteredOutByWebhookFilter' in u) {
                isFilteredOutByWebhookFilter = Boolean(u.isFilteredOutByWebhookFilter);
              }
            } catch (_) {
              // ignore parse errors
            }

            const classes = [
              "title",
              selectedIndex === index ? "selected" : "",
              searchHits.includes(index) ? "has-result" : "",
              isGroupStart ? "group-start" : "",
              hasTimeGapToNext ? "time-gap" : "",
              colorIndex ? `group-c${colorIndex}` : "",
              isFilteredOutByWebhookFilter === true ? "deemphasized" : "",
            ]
              .filter(Boolean)
              .join(" ");

            // Compute timing text relative to the first (oldest) event within the same call group
            const curMs = itemTimes[index];
            // Determine first-in-enumeration (oldest) index inside this group.
            const firstIndexInGroup = info.start + info.length - 1;
            const baseMs = itemTimes[firstIndexInGroup] ?? null;
            const isNewestInGroup = (index === info.start);
            const hasMultipleInGroup = info.length > 1;
            let timingText = '';
            let absoluteText = '';
            if (curMs != null) {
              absoluteText = formatAbsoluteTime(curMs);
              // First event in the call (displayNumber === 1) shows absolute local time
              if (displayNumber === 1 || baseMs == null) {
                timingText = absoluteText;
              } else {
                const deltaSec = (curMs - baseMs) / 1000;
                if (Number.isFinite(deltaSec)) {
                  let rounded = Math.round(deltaSec);
                  if (Object.is(rounded, -0)) rounded = 0;
                  const sign = rounded >= 0 ? '+' : '';
                  timingText = `${sign}${rounded}s`;
                } else {
                  timingText = absoluteText;
                }
              }
            }

            const matchedValRaw = singleSearchKey ? getMatchedValueForItem(item.body, singleSearchKey) : undefined;
            const matchedVal = matchedValRaw !== undefined ? formatMatchValue(matchedValRaw) : null;
            return (
              <div
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                className={classes}
                onClick={() => {
                  setSelectedIndex(index);
                }}
              >
                {/* Call group badge for visibility */}
                {item.callId ? (
                  <span className={`call-badge c${colorIndex}`} title={`Call #${item.callId}`}>
                    #{item.callId}
                  </span>
                ) : null}
                {/* Event number within the call (1..N) */}
                <span className="event-number" title={`Event ${displayNumber} in call`}>
                  {displayNumber}.
                </span>
                {/*/!* Leg chip (A/B) for quick context *!/*/}
                {/*{item.legType ? (*/}
                {/*  <span className="leg-chip" title={`Leg ${item.legType}`}>{item.legType}</span>*/}
                {/*) : null}*/}
                <span className="item-line">
                  {(() => {
                    const full = getContextInfo(item.body) || "";
                    const token = " - Dir: ";
                    if (full.includes(token)) {
                      const [left, rest] = full.split(token);
                      const right = `Dir: ${rest}`;
                      return (
                        <>
                          <span className="item-left" title={left}>{left}</span>
                          <span className="item-direction" title={right}>{right}</span>
                          {matchedVal !== null ? (
                            <span className="item-right" title={`${singleSearchKey}: ${matchedVal}`}>
                              {singleSearchKey}: {matchedVal}
                            </span>
                          ) : null}
                          {timingText ? (
                            <span className="item-time" title={absoluteText || timingText}>
                              <span className="item-time-main">{timingText}</span>
                              {isNewestInGroup && hasMultipleInGroup ? (
                                <span className="item-time-abs">{absoluteText}</span>
                              ) : null}
                            </span>
                          ) : (
                            <span className="item-time" aria-hidden="true"></span>
                          )}
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="item-left" title={full}>{full}</span>
                        <span className="item-direction" aria-hidden="true"></span>
                        {matchedVal !== null ? (
                          <span className="item-right" title={`${singleSearchKey}: ${matchedVal}`}>
                            {singleSearchKey}: {matchedVal}
                          </span>
                        ) : null}
                        {timingText ? (
                          <span className="item-time" title={absoluteText || timingText}>
                            <span className="item-time-main">{timingText}</span>
                            {isNewestInGroup && hasMultipleInGroup ? (
                              <span className="item-time-abs">{absoluteText}</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="item-time" aria-hidden="true"></span>
                        )}
                      </>
                    );
                  })()}
                </span>
              </div>
            );
          })
        : ""}
    </>
  );
};

export default SidebarItems;
